/** Read-only form view for evidence returned as structured JSON. */

import type { StructuredEvidenceDocumentResponse } from "../../../api/clinicalTrialsApiTypes";
import { StructuredEvidenceField } from "./StructuredEvidenceField";
import { StructuredEvidenceTextArea } from "./StructuredEvidenceTextArea";
import { formatStructuredEvidenceValue } from "./utils/structuredEvidenceUtils";

/** Props for a structured evidence document. */
export interface StructuredEvidencePanelProps {
  document: StructuredEvidenceDocumentResponse;
}

/** Render document metadata and extracted sections with Oasis fields. */
export function StructuredEvidencePanel({
  document,
}: StructuredEvidencePanelProps) {
  const sections = Object.entries(document.sections ?? {});

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-primaryGray-16 p-6">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-xl border border-primaryGray-14 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <StructuredEvidenceField
            id="evidence-document-type"
            label="Document Type"
            value={document.document_type}
          />
          <StructuredEvidenceField
            id="evidence-document-name"
            label="Document Name"
            value={document.document_name}
          />
          <StructuredEvidenceField
            id="evidence-document-date"
            label="Document Date"
            value={document.date}
          />
        </div>

        {sections.length > 0 ? (
          <div className="flex flex-col gap-4 border-t border-primaryGray-14 pt-5">
            <div className="text-sm font-semibold text-primaryGray-2">
              Document sections
            </div>
            {sections.map(([label, value], index) => {
              const formattedValue = formatStructuredEvidenceValue(value);
              return (
                <StructuredEvidenceTextArea
                  key={`${document.document_id}-${label}-${index}`}
                  id={`evidence-section-${index}`}
                  label={label}
                  value={formattedValue}
                />
              );
            })}
          </div>
        ) : (
          <p className="border-t border-primaryGray-14 pt-5 text-sm text-primaryGray-6">
            No document sections are available.
          </p>
        )}
      </div>
    </div>
  );
}
